import {
  Badge,
  Box,
  Collapse,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  MessageCircle,
} from 'react-feather';
import { useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { RichTextContent } from '../../../components/common/RichTextEditor';
import type {
  AuthoredAgendaSectionForCoach,
  AiPromptSelectionsDoc,
  AiPromptSegmentSelections,
  CoachSessionLeaderNote,
  CoachLeaderNotesScope,
} from '../../../services/api/coachSessionPrep';
import { AIPromptSuggestionsSection } from '../../cohorts/components/sessions/AIPromptSuggestionsSection';
import { LeaderNotesPanel } from '../../cohorts/components/sessions/LeaderNotesPanel';
import { SectionSlideThumbnails } from '../../../components/common/slides/SectionSlideThumbnails';
import {
  SectionContentBox,
  SectionNumber,
  ACCORDION_BORDER,
  ROW_EXPANDED_BG,
  STEP_CONTENT_BG,
  STEP_LABEL_COLOR,
  PARTICIPANT_BG,
  PARTICIPANT_ACCENT,
} from '../../cohorts/components/sessions/agendaSectionPrimitives';

/**
 * MED-909 AC2 — optional AI wiring threaded from the prep page. When present
 * (and AI features are enabled), each agenda section renders the shared
 * per-step AI Suggestions expandable below its content. Selections persist via
 * the peer-leader ai-selections endpoint. Absent entirely when undefined.
 */
export interface PeerLeaderAgendaAiContext {
  sessionId: string;
  groupId?: string;
  selections: AiPromptSelectionsDoc;
  onSaveSelections: (
    segmentKey: string,
    next: AiPromptSegmentSelections
  ) => Promise<void> | void;
}

/**
 * MED-1046 (E4.2a) — per-section Leader Notes wiring threaded from the prep
 * page. When present, each agenda section renders the shared `LeaderNotesPanel`
 * in single-group mode (no group selector). `notes` is the full set for the
 * session; the section filters to its own notes by stable section id (MED-1120).
 * `onSave` matches the panel's save signature (the page fixes scope='all_groups').
 */
export interface PeerLeaderAgendaLeaderNotesContext {
  notes: CoachSessionLeaderNote[];
  onSave: (args: {
    blockKey: string;
    scope: CoachLeaderNotesScope;
    groupId: string | null;
    contentHtml: string;
  }) => Promise<void>;
}

interface PeerLeaderAgendaSectionListProps {
  sections: AuthoredAgendaSectionForCoach[];
  /** Initial expanded state for all sections. Defaults to false. */
  startCollapsed?: boolean;
  /** MED-909 AC2 — when provided, renders per-step AI Suggestions. */
  aiContext?: PeerLeaderAgendaAiContext;
  /** MED-1046 — when provided, renders the per-section Leader Notes field. */
  leaderNotesContext?: PeerLeaderAgendaLeaderNotesContext;
  /**
   * MED-1079 — the loaded slide-deck PDF. When present, a section's associated
   * slides render as thumbnails (matching the content editor); while loading or
   * absent the renderer falls back to `📄 Page N` chips. The page loads the deck
   * once via `useSlideDeckDoc` and passes it down here.
   */
  slideDeckDoc?: PDFDocumentProxy | null;
}

/**
 * AC9 — Peer-leader Step 2 renderer for the authored Session Guide.
 *
 * MED-1092 — visually mirrors the coach `AuthoredSessionAgendaAccordion`
 * (MED-1093): a single outlined accordion container; navy numbered section
 * circles; the section's leader prompt(s) + slides inside a neutral "Step
 * Content" box; the read-only participant prompt in a green "What participants
 * see" box. Both boxes + the section number come from the shared
 * `agendaSectionPrimitives` so the coach and peer agendas can't drift. The
 * peer-only wiring is unchanged: an editable per-section Leader Notes field
 * (MED-1046) and, when AI features are on, the per-step AI Suggestions
 * (MED-909, keyed by stable section id).
 */
export function PeerLeaderAgendaSectionList({
  sections,
  startCollapsed = false,
  aiContext,
  leaderNotesContext,
  slideDeckDoc = null,
}: PeerLeaderAgendaSectionListProps) {
  const sorted = sections.slice().sort((a, b) => a.sort_order - b.sort_order);
  return (
    <Box
      style={{
        border: `1px solid ${ACCORDION_BORDER}`,
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {sorted.map((section, idx) => (
        <SectionRow
          key={section.id}
          section={section}
          sectionIdx={idx}
          isLast={idx === sorted.length - 1}
          startCollapsed={startCollapsed}
          aiContext={aiContext}
          leaderNotesContext={leaderNotesContext}
          slideDeckDoc={slideDeckDoc}
        />
      ))}
    </Box>
  );
}

interface SectionRowProps {
  section: AuthoredAgendaSectionForCoach;
  sectionIdx: number;
  isLast: boolean;
  startCollapsed: boolean;
  aiContext?: PeerLeaderAgendaAiContext;
  leaderNotesContext?: PeerLeaderAgendaLeaderNotesContext;
  slideDeckDoc: PDFDocumentProxy | null;
}

function SectionRow({
  section,
  sectionIdx,
  isLast,
  startCollapsed,
  aiContext,
  leaderNotesContext,
  slideDeckDoc,
}: SectionRowProps) {
  const [open, setOpen] = useState(!startCollapsed);
  const sectionTitle = section.section_name ?? 'Untitled section';

  const sortedPrompts = section.leader_prompts
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const sortedVisuals = section.visuals
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const hasStepContent = sortedPrompts.length > 0 || sortedVisuals.length > 0;

  return (
    <div
      style={{
        borderBottom: isLast ? undefined : `1px solid ${ACCORDION_BORDER}`,
        background: open ? ROW_EXPANDED_BG : undefined,
      }}
    >
      <UnstyledButton
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'block',
          width: '100%',
          padding: 'var(--mantine-spacing-md) var(--mantine-spacing-lg)',
        }}
        aria-expanded={open}
      >
        <Group justify="space-between" align="center">
          <Group align="center" gap="md">
            <SectionNumber n={sectionIdx + 1} active={open} />
            <Text fw={600}>{sectionTitle}</Text>
          </Group>
          <Group gap="sm" align="center">
            {section.duration_minutes !== null && (
              <Group gap={4} align="center">
                <Clock size={14} />
                <Text size="sm" c="dimmed">
                  {section.duration_minutes} min
                </Text>
              </Group>
            )}
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </Group>
        </Group>
      </UnstyledButton>

      <Collapse in={open}>
        <Stack
          gap="md"
          style={{
            padding: '0 var(--mantine-spacing-lg) var(--mantine-spacing-md)',
          }}
        >
          {/* "Step Content" box: the section's leader prompt(s) with the
              associated slides pulled in below the text (thumbnails once the deck
              PDF is loaded; otherwise `Page N` chips). */}
          {hasStepContent && (
            <SectionContentBox
              icon={
                <FileText
                  size={12}
                  color={STEP_LABEL_COLOR}
                  aria-hidden="true"
                />
              }
              label="Step Content"
              labelColor={STEP_LABEL_COLOR}
              background={STEP_CONTENT_BG}
            >
              {sortedPrompts.length > 0 && (
                <Stack gap="sm">
                  {sortedPrompts.map((prompt, promptIdx) => {
                    const hasIntro =
                      !!prompt.intro_text &&
                      prompt.intro_text.trim().length > 0;
                    const hasBody =
                      !!prompt.discussion_prompt &&
                      prompt.discussion_prompt.trim().length > 0;
                    if (!hasIntro && !hasBody) return null;
                    return (
                      <div key={promptIdx}>
                        {hasIntro && (
                          <Text size="xs" c="dimmed" fw={600} mb={4}>
                            {prompt.intro_text!.trim()}
                          </Text>
                        )}
                        {hasBody && (
                          <RichTextContent
                            content={prompt.discussion_prompt}
                            emptyText=""
                          />
                        )}
                      </div>
                    );
                  })}
                </Stack>
              )}

              {sortedVisuals.length > 0 && (
                <Box mt={sortedPrompts.length > 0 ? 'sm' : 0}>
                  {slideDeckDoc ? (
                    <SectionSlideThumbnails
                      doc={slideDeckDoc}
                      visuals={sortedVisuals}
                    />
                  ) : (
                    <Group gap="xs" align="center">
                      {sortedVisuals.map(v => (
                        <Badge
                          key={v.slide_page}
                          variant="light"
                          color="gray"
                          radius="xl"
                          leftSection={<FileText size={10} />}
                          styles={{
                            label: {
                              fontSize: 12,
                              fontWeight: 600,
                              transform: 'translateY(1px)',
                            },
                          }}
                        >
                          Page {v.slide_page}
                        </Badge>
                      ))}
                    </Group>
                  )}
                </Box>
              )}
            </SectionContentBox>
          )}

          {section.participant_prompt &&
            section.participant_prompt.trim().length > 0 && (
              <SectionContentBox
                icon={
                  <MessageCircle
                    size={12}
                    color={PARTICIPANT_ACCENT}
                    aria-hidden="true"
                  />
                }
                label="What participants see"
                labelColor={PARTICIPANT_ACCENT}
                background={PARTICIPANT_BG}
              >
                <RichTextContent
                  content={section.participant_prompt}
                  emptyText=""
                />
              </SectionContentBox>
            )}

          {/* MED-1046 (E4.2a) — per-section Leader Notes (peer leader self-notes),
              single-group mode (no group selector). MED-1120 — keyed by the
              stable section id so notes persist across CM content edits. */}
          {leaderNotesContext && (
            <LeaderNotesPanel
              singleGroup
              blockKey={section.id}
              groups={[]}
              notes={leaderNotesContext.notes.filter(
                n => n.block_key === section.id
              )}
              onSave={leaderNotesContext.onSave}
            />
          )}

          {/* MED-909 AC2 — per-step AI Suggestions (purple), keyed by section id. */}
          {aiContext && (
            <AIPromptSuggestionsSection
              sessionId={aiContext.sessionId}
              groupId={aiContext.groupId}
              segmentIndex={sectionIdx}
              segmentKey={section.id}
              currentSelections={aiContext.selections[section.id]}
              onSelectionsChange={next =>
                aiContext.onSaveSelections(section.id, next)
              }
            />
          )}
        </Stack>
      </Collapse>
    </div>
  );
}
